<template>
  <div class="accent" v-if="!isExternalAssessor">
    <v-container fluid class="pa-3 pa-md-4 dash-wrap">
      <v-card class="dash-welcome" elevation="0">
        <div class="dash-welcome-text">
          <app-welcome-wordings></app-welcome-wordings>
        </div>
        <div class="dash-welcome-art">
          <img src="@/assets/img/Group.png" alt="" class="dash-art-dots" />
          <img src="@/assets/img/Face.png" alt="" class="dash-art-face" />
        </div>
      </v-card>

      <app-assessment-centre-updates
        :summary="assessmentCentreSummary"
        :isLoading="isFetchingAssessmentCentreSummary"
      ></app-assessment-centre-updates>

      <app-garage-updates
        :summary="garageSummary"
        :isLoading="isFetchingGarageSummary"
      ></app-garage-updates>
    </v-container>
  </div>

  <div v-else>
    <app-external-assessor-dashboard
      :summary="extAssessorSummary"
      :isLoading="isFetchingExtAssessorSummary"
    ></app-external-assessor-dashboard>
  </div>
</template>

<script>
import axios from 'axios'
import { viewPermissions } from '@/utils/roles'
import { mapState } from 'vuex'
import moment from 'moment'

export default {
  components: {
    appAssessmentCentreUpdates: () =>
      import('@/components/dashboard/AssessmentCentreUpdates'),
    appGarageUpdates: () => import('@/components/dashboard/GarageUpdates'),
    appExternalAssessorDashboard: () =>
      import('@/components/dashboard/ExternalAssessorDashboard'),
    appWelcomeWordings: () => import('@/components/dashboard/Welcome'),
  },
  props: {
    source: String,
  },
  data: () => ({
    dialog: false,
    drawer: null,
    isFetchingAssessmentCentreSummary: false,
    assessmentCentreSummary: {
      bookedVehicles: null,
      supplementaryReceived: null,
      submittedBids: null,
      approvedBids: null,
      writeOf: null,
      belowExcess: null,
      cashInLieu: null,
      declined: null,
      undocumented: null,
      underInvestigation: null,
      checkedOut: null,
    },
    isFetchingGarageSummary: false,
    garageSummary: {
      total_vehicles: null,
      repairs_count: null,
      released_count: null,
    },
    isFetchingExtAssessorSummary: false,
    extAssessorSummary: {
      bidForms: 0,
      reinspections: 0,
      supplementaries: 0,
    },
    refreshTimer: null,
  }),
  computed: {
    isExternalAssessor() {
      return !this.viewPermissions([
        'internalAssessor',
        'technician',
        'admin',
        'claimOfficer',
        'sectionHead',
        'customerService',
      ])
    },

    ...mapState({
      user: (state) => state.auth.user,
    }),
    greeting() {
      const currentHour = moment().hour()

      if (currentHour >= 0 && currentHour < 12) {
        return 'Good Morning,'
      } else if (currentHour >= 12 && currentHour < 16) {
        return 'Good Afternoon,'
      } else {
        return 'Good Evening,'
      }
    },
  },
  methods: {
    viewPermissions,
    async fetchAssessmentCentreSummary() {
      this.isFetchingAssessmentCentreSummary = true

      await axios
        .get('motor-assessment/api/dashboard/live/count')
        .then(({ data }) => {
          if (data.success) {
            this.assessmentCentreSummary = { ...data.data }
          }
        })
        .catch((err) => {
          this.$store.commit('SNACKBAR', {
            state: 'failed',
            message:
              err.message ?? 'Failed to load assessment centre summary count',
            variant: 'error',
          })
        })
        .finally(() => (this.isFetchingAssessmentCentreSummary = false))
    },
    async fetchGarageSummary() {
      this.isFetchingGarageSummary = true

      await axios
        .get('/motor-assessment/api/dashboard/garage/count')
        .then(({ data }) => {
          if (data.success) {
            this.garageSummary = { ...data.data }
          }
        })
        .catch((err) => {
          this.$store.commit('SNACKBAR', {
            state: 'failed',
            message: err.message ?? 'Failed to load garage summary count',
            variant: 'error',
          })
        })
        .finally(() => (this.isFetchingGarageSummary = false))
    },

    async fetchExtAssessorSummary() {
      this.isFetchingExtAssessorSummary = true

      await axios
        .get(
          `/motor-assessment/api/dashboard/external-assessor/count/${this.$store.state.auth.user.sub}`
        )
        .then(({ data }) => {
          if (data.success) {
            this.extAssessorSummary = data.data
          } else {
            this.$store.commit('SNACKBAR', {
              state: 'failed',
              message: 'Failed to retrieve summary count',
              variant: 'error',
            })
          }
        })
        .catch((err) => {
          this.$store.commit('SNACKBAR', {
            state: 'failed',
            message: err.message ?? 'Failed to retrieve summary count',
            variant: 'error',
          })
        })
        .finally(() => (this.isFetchingExtAssessorSummary = false))
    },
    startAutoRefresh() {
      const refreshInterval = 5 * 60 * 1000

      if (this.refreshTimer) clearInterval(this.refreshTimer)

      this.refreshTimer = setInterval(() => {
        if (!this.isExternalAssessor) {
          this.fetchGarageSummary()
          this.fetchAssessmentCentreSummary()
        } else {
          this.fetchExtAssessorSummary()
        }
      }, refreshInterval)
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
  },
  beforeMount() {
    if (!this.isExternalAssessor) {
      this.fetchGarageSummary()
      this.fetchAssessmentCentreSummary()
    } else {
      this.fetchExtAssessorSummary()
    }
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
}
</script>

<style scoped>
.dash-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Welcome banner */
.dash-welcome {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid #e8edf5;
  background: linear-gradient(135deg, #ffffff 0%, #f4f8ff 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 26px;
  min-height: 128px;
}
.dash-welcome-text {
  min-width: 0;
  flex: 1 1 auto;
}
.dash-welcome-art {
  position: relative;
  flex: 0 0 auto;
  width: 260px;
  height: 96px;
  display: none;
}
.dash-art-face {
  display: block;
  height: 96px;
  margin-left: auto;
}
.dash-art-dots {
  position: absolute;
  top: 6px;
  left: 24px;
  height: 70px;
  opacity: 0.9;
}

@media (min-width: 960px) {
  .dash-welcome-art {
    display: block;
  }
}
</style>
